/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Edición del nombre visible del dueño (customer-facing en turnos/agenda).
 */
import React, { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { ModalShell } from '../../shared/modals/ModalShell';
import { ModalFormField } from '../../shared/modals/ModalFormField';
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { settingsStyles } from '../screens/settingsScreen.styles';
import { ticketColors } from '../../shared/theme/ticketColors';

type Props = {
  visible: boolean;
  initialValue: string;
  saving: boolean;
  onSave: (name: string) => void;
  onClose: () => void;
};

export function DisplayNameEditModal({ visible, initialValue, saving, onSave, onClose }: Props) {
  const [draft, setDraft] = useState(initialValue);

  useEffect(() => {
    if (visible) setDraft(initialValue);
  }, [visible, initialValue]);

  const canSave = draft.trim().length > 0 && draft.trim() !== initialValue.trim() && !saving;

  return (
    <ModalShell
      visible={visible}
      onClose={() => {
        if (!saving) onClose();
      }}
      title="Tu nombre"
      widthVariant="compact"
      scrollable={false}
      footer={
        <View style={modalExperience.detailActionsGrid}>
          <TouchableOpacity
            style={[modalExperience.detailActionBtn, modalExperience.detailActionBtnPrimary]}
            onPress={() => onSave(draft.trim())}
            disabled={!canSave}
            accessibilityRole="button"
            accessibilityLabel="Guardar nombre"
          >
            {saving ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text
                style={[
                  modalExperience.detailActionText,
                  modalExperience.detailActionTextPrimary,
                  !canSave && styles.disabledText,
                ]}
              >
                Guardar
              </Text>
            )}
          </TouchableOpacity>
        </View>
      }
    >
      <ModalFormField label="¿Cómo te llamás?">
        <TextInput
          value={draft}
          onChangeText={setDraft}
          placeholder="Ej. Juan"
          placeholderTextColor={ticketColors.ink3}
          style={settingsStyles.input}
          editable={!saving}
          maxLength={120}
          autoFocus
          returnKeyType="done"
          onSubmitEditing={() => {
            if (canSave) onSave(draft.trim());
          }}
          accessibilityLabel="Tu nombre"
          testID="profile.display-name-input"
        />
      </ModalFormField>
      <Text style={styles.hint}>Así te ven tus clientes en los turnos.</Text>
    </ModalShell>
  );
}

const styles = StyleSheet.create({
  hint: {
    fontSize: 12,
    lineHeight: 17,
    color: ticketColors.ink3,
    marginTop: 6,
  },
  disabledText: {
    opacity: 0.6,
  },
});
